import debounce from 'lodash.debounce';
import { isToday } from '@/util/date';

/**
 * Codmon DatePagerディレクティブ
 *
 * @description
 * 日付や期間を前後に切り替えるナビゲーションコンポーネント（Design SystemのDatePagerに準拠）。
 * 前後ナビゲーションボタン・期間ラベル・カレンダー（ドロップダウン）で構成される。
 * 現状は unit="day" のみ実装しており、Month / Week / FiscalYear は将来拡張のためpropだけ用意している。
 * カレンダーは Angular UI Bootstrap の datepicker-popup を内蔵する。
 *
 * @example
 * // デイリーボード（Day単位・年表示・カレンダー有効・当日ボタン有効）
 * <codmon-date-pager
 *   date="fv.date"
 *   unit="day"
 *   size="medium"
 *   show-year="true"
 *   show-dropdown="true"
 *   show-today-button="true"
 *   on-prev="moveDays(-1)"
 *   on-next="moveDays(1)"
 *   on-select="memberList()">
 * </codmon-date-pager>
 *
 * @property {Date} date - 表示日付（双方向バインド。カレンダー選択・当日ボタンで更新される）
 * @property {string} [unit='day'] - 期間単位（現状 'day' のみ対応）
 * @property {string} [size='medium'] - サイズ（'medium' | 'small'）
 * @property {boolean} [responsive=false] - 画面幅が狭い時（iPad横=1024px以下）に自動で small へ縮小するか
 * @property {boolean} [showYear=false] - 年プレフィックスの表示
 * @property {boolean} [showDropdown=true] - カレンダー（ドロップダウン）の有効化
 * @property {boolean} [showTodayButton=false] - 「今日を表示」ボタンの表示（当日表示中は非活性）
 * @property {Function} onPrev - 「前へ」ボタンクリック時のコールバック
 * @property {Function} onNext - 「次へ」ボタンクリック時のコールバック
 * @property {Function} onSelect - カレンダー選択・当日ボタンで日付が変わった時のコールバック
 */
angular.module('inspinia').directive('codmonDatePager', [
  '$timeout',
  '$window',
  ($timeout, $window) => ({
    restrict: 'E',
    scope: {
      date: '=',
      unit: '@',
      responsive: '<?',
      showYear: '<?',
      showDropdown: '<?',
      showTodayButton: '<?',
      onPrev: '&',
      onNext: '&',
      onSelect: '&',
    },
    templateUrl: `/manage/src/view/directive/codmon-ui/codmon_date_pager.html?${new Date().getTime()}`,
    link($scope, $element, $attrs) {
      const units = ['day'];
      $scope.unit = units.includes($scope.unit) ? $scope.unit : 'day';

      // size は @ バインドにすると attrs.$observe が link 後に非同期で $scope.size を
      // 上書きし、responsive で算出した実効サイズを打ち消してしまう。attrs から同期的に
      // 読み取り、$scope.size は applyEffectiveSize で自前管理する。
      const sizes = ['medium', 'small'];
      const configuredSize = sizes.includes($attrs.size) ? $attrs.size : 'medium';
      $scope.responsive = $scope.responsive === undefined ? false : !!$scope.responsive;

      $scope.showYear = $scope.showYear === undefined ? false : !!$scope.showYear;
      $scope.showDropdown = $scope.showDropdown === undefined ? true : !!$scope.showDropdown;
      $scope.showTodayButton = $scope.showTodayButton === undefined ? false : !!$scope.showTodayButton;

      // responsive時、iPad横(1024px)以下では small に縮小する。
      // size を切り替えることでフォント・枠線・キャレット・ナビボタンサイズが一括で small になる。
      const RESPONSIVE_MAX_WIDTH = 1024;
      const applyEffectiveSize = () => {
        const size = $scope.responsive && $window.innerWidth <= RESPONSIVE_MAX_WIDTH ? 'small' : configuredSize;
        $scope.size = size;
        // codmon-icon-button のサイズへ対応（medium=32px/icon24, small=24px/icon20）
        $scope.navButtonSize = size === 'small' ? 'xsmall' : 'small';
      };
      applyEffectiveSize();

      if ($scope.responsive) {
        const onResize = debounce(() => {
          applyEffectiveSize();
          $scope.$applyAsync();
        }, 150);
        angular.element($window).on('resize', onResize);
        $scope.$on('$destroy', () => angular.element($window).off('resize', onResize));
      }

      $scope.isOpen = false;

      // datepicker-popup は開いた瞬間に外側クリック監視を登録し、その監視が「伝播中のクリック」を
      // 拾って即座に閉じてしまう。$timeout で伝播完了後に開くことで回避する（親の ng-click も殺さない）。
      $scope.openCalendar = () => {
        if (!$scope.showDropdown || $scope.isOpen) return;
        $timeout(() => {
          $scope.isOpen = true;
        });
      };

      // Enter / Space で開く（閉じるは datepicker-popup 側の Escape が担う）
      $scope.onLabelKeydown = ($event) => {
        if (!$scope.showDropdown) return;
        if ($event.key === 'Enter' || $event.key === ' ' || $event.key === 'Spacebar') {
          $event.preventDefault();
          $scope.openCalendar();
        }
      };

      $scope.codmonDatePagerClasses = () => ({
        'codmon-date-pager': true,
        [`codmon-date-pager--${$scope.size}`]: true,
      });

      // isToday は現在日時を参照するため、date 変更に加えて0時跨ぎでも watch が再評価される
      $scope.isTodayFlag = false;
      $scope.$watch(
        () => isToday($scope.date),
        (value) => {
          $scope.isTodayFlag = value;
        }
      );

      $scope.handlePrev = () => $scope.onPrev();
      $scope.handleNext = () => $scope.onNext();
      // ng-change は双方向バインドの親反映より先に発火するため、$timeout で反映後に onSelect を呼ぶ
      $scope.handleSelect = () => $timeout(() => $scope.onSelect());

      $scope.goToday = () => {
        if ($scope.isTodayFlag) return;
        $scope.date = new Date();
        $timeout(() => $scope.onSelect());
      };
    },
  }),
]);
